// vg-binding: CONTEXT:decisions  (P999.11.D-09/D-14/D-15/D-16 tokens + D-04 dark-ok)
// vg-binding: INTERFACE-STANDARDS:error-shape  (pure FE restyle — no API surface changed)
// apps/admin/src/pages/billing/ImapConfigPage.tsx
//
// Phase 3.2 Task 34 — Admin IMAP Config page.
//
// Per CONTEXT.md (bank email auto-approve — IMAP host/port +
// per-gateway regex matrix + Test parse). Covers goal G-IMAP-06.
//
// Endpoints (Per API-CONTRACTS.md L2320-2512):
//   GET  /api/v1/admin/imap/config
//   PUT  /api/v1/admin/imap/config
//   POST /api/v1/admin/imap/test-parse
//   GET  /api/v1/admin/imap/logs
//
// Design: admin-imap-config.png — Connection card (host/port/email/password
// + poll interval slider), Parser Test panel (paste sample → show parsed
// txn_id/amount/sender), per-gateway regex matrix tab, Logs tab.

import { useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
  useToast,
  PageHeader,
} from '@printway/ui';
import { apiClient } from '@printway/shared/api';

import {
  envelopeError,
  newIdempotencyKey,
  unwrap,
  type LooseClient,
} from './billing-shared.js';
import { useTranslation } from '../../i18n/useTranslation.js';

type TFn = (key: string, vars?: Record<string, unknown>) => string;

// Tier 2 manual gateways (each forwards confirmation
// emails that IMAP parses). Tier 1 PayPal/Stripe are NOT in the matrix
// (instant webhook flow, no IMAP fallback).
const IMAP_GATEWAYS = ['sunrate', 'pingpong', 'worldfirst', 'airwallex', 'bank'] as const;
type ImapGateway = (typeof IMAP_GATEWAYS)[number];

interface GatewayParser {
  gateway: string;
  sender_regex: string;
  txn_id_regex: string;
  amount_regex: string;
}

interface ImapConfig {
  host: string;
  port: number;
  secure: boolean;
  source_namespace: string;
  email: string;
  poll_interval_sec: number;
  gateways: GatewayParser[];
}

interface ImapLogEntry {
  ts: string;
  sender: string;
  gateway: string | null;
  matched: boolean;
  extracted: { txn_id: string | null; amount: string | null };
  linked_request_id: string | null;
}

const DEFAULT_CONFIG: ImapConfig = {
  host: 'imap.gmail.com',
  port: 993,
  secure: true,
  source_namespace: '',
  email: '',
  poll_interval_sec: 300,
  gateways: IMAP_GATEWAYS.map((g) => ({
    gateway: g,
    sender_regex: '',
    txn_id_regex: '',
    amount_regex: '',
  })),
};

// Per goal: source namespace must NOT equal generic word "imap".
// Validates ISO-id-style identifier OR empty (server may default).
const SOURCE_NAMESPACE_PATTERN = /^[a-z][a-z0-9_-]*$/i;
function validateSourceNamespace(value: string | undefined | null, t: TFn): string | null {
  const trimmed = (value ?? '').trim();
  if (trimmed.length === 0) {
    return t('imap.validation.nsRequired');
  }
  if (!SOURCE_NAMESPACE_PATTERN.test(trimmed)) {
    return t('imap.validation.nsFormat');
  }
  if (trimmed.toLowerCase() === 'imap') {
    return t('imap.validation.nsForbidden');
  }
  return null;
}

type Tab = 'connection' | 'matrix' | 'logs';

export function ImapConfigPage(): JSX.Element {
  const { toast } = useToast();
  const { t } = useTranslation('finance');
  const qc = useQueryClient();

  const [tab, setTab] = useState<Tab>('connection');
  const [config, setConfig] = useState<ImapConfig>(DEFAULT_CONFIG);
  const [password, setPassword] = useState(''); // write-only per contract Block 2
  const [parserGateway, setParserGateway] = useState<ImapGateway>('sunrate');
  const [sampleEmail, setSampleEmail] = useState('');
  const [parseResult, setParseResult] = useState<{
    matched: boolean;
    extracted: { txn_id: string | null; amount: string | null };
    would_link_to: string | null;
  } | null>(null);

  const cfgQuery = useQuery({
    queryKey: ['admin', 'imap-config'],
    queryFn: async (): Promise<ImapConfig> => {
      const c = apiClient() as unknown as LooseClient;
      const { data, error, response } = await c.GET('/api/v1/admin/imap/config');
      if (error || !response.ok) {
        throw new Error(envelopeError(error) ?? `HTTP ${response.status}`);
      }
      return unwrap<ImapConfig>(data);
    },
  });

  useEffect(() => {
    if (cfgQuery.data) {
      // Ensure all 5 IMAP gateways have a row (server may return partial set).
      const merged = IMAP_GATEWAYS.map((g) => {
        const existing = cfgQuery.data!.gateways.find((row) => row.gateway === g);
        return (
          existing ?? {
            gateway: g,
            sender_regex: '',
            txn_id_regex: '',
            amount_regex: '',
          }
        );
      });
      setConfig({ ...cfgQuery.data, gateways: merged });
    }
  }, [cfgQuery.data]);

  const dirty = useMemo(() => {
    if (!cfgQuery.data) return password.length > 0;
    return JSON.stringify(config) !== JSON.stringify(cfgQuery.data) || password.length > 0;
  }, [config, cfgQuery.data, password]);

  const sourceNamespaceError = validateSourceNamespace(config.source_namespace, t);

  const saveMutation = useMutation({
    mutationFn: async (): Promise<ImapConfig> => {
      // Source namespace validation — must be non-empty, valid id, ≠ 'imap'.
      if (sourceNamespaceError) {
        throw new Error(`${t('imap.fields.sourceNamespace')}: ${sourceNamespaceError}`);
      }
      // Reject obviously invalid regex inputs client-side (Rule 5).
      for (const row of config.gateways) {
        for (const field of ['sender_regex', 'txn_id_regex', 'amount_regex'] as const) {
          const value = row[field];
          if (value === '') continue;
          try {
            new RegExp(value);
          } catch {
            throw new Error(
              t('imap.validation.invalidRegex', { gateway: row.gateway, field }),
            );
          }
        }
      }
      const c = apiClient() as unknown as LooseClient;
      const body: ImapConfig & { password?: string } = { ...config };
      if (password) body.password = password;
      const { data, error, response } = await c.PUT('/api/v1/admin/imap/config', {
        headers: { 'Idempotency-Key': newIdempotencyKey() },
        body,
      });
      if (error || !response.ok) {
        throw new Error(envelopeError(error) ?? `HTTP ${response.status}`);
      }
      return unwrap<ImapConfig>(data);
    },
    onSuccess: (saved) => {
      qc.setQueryData(['admin', 'imap-config'], saved);
      setPassword('');
      toast({ variant: 'success', title: t('imap.toast.savedTitle') });
    },
    onError: (err) => {
      toast({
        variant: 'error',
        title: t('imap.toast.saveFailedTitle'),
        description: err instanceof Error ? err.message : t('imap.toast.networkError'),
      });
    },
  });

  const testParseMutation = useMutation({
    mutationFn: async (): Promise<{
      matched: boolean;
      extracted: { txn_id: string | null; amount: string | null };
      would_link_to: string | null;
    }> => {
      const c = apiClient() as unknown as LooseClient;
      // dbg #2730 — this route is idempotency-gated (createIdempotencyMiddleware
      // 'api:admin-imap-test-parse'); without the header the server returns 400
      // IDEMPOTENCY_KEY_REQUIRED and the result panel stays empty. The BE body
      // contract is `email_text` (not `sample_email_text`).
      const { data, error, response } = await c.POST('/api/v1/admin/imap/test-parse', {
        headers: { 'Idempotency-Key': newIdempotencyKey() },
        body: { email_text: sampleEmail, gateway: parserGateway },
      });
      if (error || !response.ok) {
        throw new Error(envelopeError(error) ?? `HTTP ${response.status}`);
      }
      return unwrap<{
        matched: boolean;
        extracted: { txn_id: string | null; amount: string | null };
        would_link_to: string | null;
      }>(data);
    },
    onSuccess: (res) => setParseResult(res),
    onError: (err) => {
      toast({
        variant: 'error',
        title: t('imap.toast.testParseFailedTitle'),
        description: err instanceof Error ? err.message : t('imap.toast.networkError'),
      });
    },
  });

  // Logs tab — paginated
  const logsQuery = useQuery({
    queryKey: ['admin', 'imap-logs'],
    enabled: tab === 'logs',
    queryFn: async (): Promise<ImapLogEntry[]> => {
      const c = apiClient() as unknown as LooseClient;
      const { data, error, response } = await c.GET('/api/v1/admin/imap/logs?limit=100');
      if (error || !response.ok) {
        throw new Error(envelopeError(error) ?? `HTTP ${response.status}`);
      }
      const wrapped = unwrap<{ data: ImapLogEntry[] }>(data);
      return wrapped.data ?? [];
    },
  });

  function updateGatewayCell(
    gateway: string,
    field: 'sender_regex' | 'txn_id_regex' | 'amount_regex',
    value: string,
  ): void {
    setConfig((prev) => ({
      ...prev,
      gateways: prev.gateways.map((row) =>
        row.gateway === gateway ? { ...row, [field]: value } : row,
      ),
    }));
  }

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-8">
      <PageHeader title={t('imap.page.heading')} subtitle={t('imap.page.subtitle')} />

      {/* ---- Tab nav ---- */}
      <div className="flex gap-2 rounded-lg border border-pw-border bg-pw-bg p-2">
        {([
          ['connection', t('imap.tabs.connection')],
          ['matrix', t('imap.tabs.matrix')],
          ['logs', t('imap.tabs.logs')],
        ] as const).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={cn(
              'rounded-md px-4 py-2 text-sm font-medium transition-colors',
              tab === id
                ? 'bg-pw-primary text-white'
                : 'text-pw-text-muted hover:bg-pw-surface hover:text-pw-text',
            )}
            data-testid={`imap-tab-${id}`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ---- Connection tab ---- */}
      {tab === 'connection' ? (
        <>
          <section className="rounded-xl border border-pw-border bg-pw-bg p-6">
            <h2 className="text-lg font-semibold text-pw-text">{t('imap.connection.heading')}</h2>
            {/*
              Wrap the connection fields in a real `<form>` so the password
              input is "contained in a form" — Chrome warns + refuses to
              offer the password manager API otherwise (Codex C-Bug-32).
              `autoComplete="off"` + `type="password"` field has
              `autoComplete="new-password"` so the browser stops trying to
              autofill admin SSO credentials into a backend IMAP secret
              field (Bug-21 + Bug-24 root cause). Submit is no-op — the
              real save fires from the bottom Save button + saveMutation.
            */}
            <form
              onSubmit={(e) => e.preventDefault()}
              autoComplete="off"
              className="mt-6 grid gap-6 sm:grid-cols-2"
            >
              <div className="flex flex-col gap-2">
                <label htmlFor="imap-host" className="text-sm font-medium text-pw-text">
                  {t('imap.connection.host')}
                </label>
                <Input
                  id="imap-host"
                  value={config.host}
                  onChange={(e) => setConfig((p) => ({ ...p, host: e.target.value }))}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="imap-port" className="text-sm font-medium text-pw-text">
                  {t('imap.connection.port')}
                </label>
                <Input
                  id="imap-port"
                  type="number"
                  min={1}
                  max={65535}
                  value={String(config.port)}
                  onChange={(e) =>
                    setConfig((p) => ({ ...p, port: Math.max(1, Number(e.target.value) || 0) }))
                  }
                />
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="imap-secure" className="text-sm font-medium text-pw-text">
                  {t('imap.connection.secure')}
                </label>
                <label className="flex h-10 cursor-pointer items-center gap-2 rounded-md border border-pw-border bg-pw-bg px-3">
                  <input
                    id="imap-secure"
                    type="checkbox"
                    checked={config.secure ?? true}
                    onChange={(e) =>
                      setConfig((p) => ({ ...p, secure: e.target.checked }))
                    }
                    className="h-4 w-4 rounded border-pw-border text-pw-primary focus:ring-pw-primary"
                    data-testid="imap-secure"
                  />
                  <span className="text-sm text-pw-text">
                    {t('imap.connection.secureHint')}
                  </span>
                </label>
              </div>
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="imap-source-namespace"
                  className="text-sm font-medium text-pw-text"
                >
                  {t('imap.fields.sourceNamespace')} <span className="text-rose-600 dark:text-rose-300">*</span>
                </label>
                <Input
                  id="imap-source-namespace"
                  value={config.source_namespace ?? ''}
                  onChange={(e) =>
                    setConfig((p) => ({ ...p, source_namespace: e.target.value }))
                  }
                  placeholder={t('imap.connection.namespacePlaceholder')}
                  aria-invalid={
                    validateSourceNamespace(config.source_namespace, t) ? 'true' : 'false'
                  }
                  data-testid="imap-source-namespace"
                />
                {validateSourceNamespace(config.source_namespace, t) ? (
                  <p
                    className="text-xs text-rose-600 dark:text-rose-300"
                    role="alert"
                    data-testid="imap-source-namespace-error"
                  >
                    {validateSourceNamespace(config.source_namespace, t)}
                  </p>
                ) : (
                  <p className="text-xs text-pw-text-muted">
                    {t('imap.connection.namespaceHint')}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="imap-email" className="text-sm font-medium text-pw-text">
                  {t('imap.connection.email')}
                </label>
                <Input
                  id="imap-email"
                  type="email"
                  value={config.email}
                  onChange={(e) => setConfig((p) => ({ ...p, email: e.target.value }))}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="imap-pwd" className="text-sm font-medium text-pw-text">
                  {t('imap.connection.password')}
                </label>
                <Input
                  id="imap-pwd"
                  type="password"
                  // new-password disables Chrome's autofill of stored
                  // admin/SSO creds into this secret field (Bug-21 + 24).
                  autoComplete="new-password"
                  name="imap-app-password"
                  placeholder={cfgQuery.data ? '••••••••••••••' : t('imap.connection.passwordPlaceholder')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </form>

            <div className="mt-6">
              <label
                htmlFor="imap-poll"
                className="mb-2 block text-sm font-semibold text-pw-text"
              >
                {t('imap.connection.pollInterval')}
              </label>
              <input
                id="imap-poll"
                type="range"
                min={60}
                max={900}
                step={60}
                value={config.poll_interval_sec}
                onChange={(e) =>
                  setConfig((p) => ({ ...p, poll_interval_sec: Number(e.target.value) }))
                }
                className="w-full accent-pw-primary-strong"
                data-testid="imap-poll-interval"
              />
              <p className="mt-2 text-sm text-pw-text-muted">
                {t('imap.connection.pollCurrent', { minutes: Math.round(config.poll_interval_sec / 60) })}
              </p>
            </div>
          </section>

          {/* ---- Parser Test ---- */}
          <section className="rounded-xl border border-pw-border bg-pw-bg p-6">
            <h2 className="text-lg font-semibold text-pw-text">{t('imap.parserTest.heading')}</h2>
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="flex flex-col gap-2 sm:w-48">
                <label htmlFor="parser-gateway" className="text-xs font-medium text-pw-text">
                  {t('imap.parserTest.gatewayLabel')}
                </label>
                <Select
                  value={parserGateway}
                  onValueChange={(value) => setParserGateway(value as ImapGateway)}
                >
                  <SelectTrigger id="parser-gateway">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {IMAP_GATEWAYS.map((g) => (
                      <SelectItem key={g} value={g}>
                        {g}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                disabled={sampleEmail.trim().length === 0 || testParseMutation.isPending}
                onClick={() => testParseMutation.mutate()}
                data-testid="imap-test-parse-btn"
              >
                {testParseMutation.isPending ? t('imap.parserTest.testing') : t('imap.parserTest.testButton')}
              </Button>
            </div>
            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              <textarea
                rows={8}
                placeholder={t('imap.parserTest.samplePlaceholder')}
                className="w-full rounded-lg border border-pw-border bg-pw-bg p-3 font-mono text-xs"
                value={sampleEmail}
                onChange={(e) => setSampleEmail(e.target.value)}
                data-testid="imap-sample-email"
              />
              <div className="rounded-lg bg-pw-primary/10 p-4 text-sm">
                <p className="font-semibold text-pw-primary-strong">{t('imap.parserTest.resultHeading')}</p>
                {parseResult ? (
                  parseResult.matched ? (
                    <div className="mt-2 space-y-1 text-pw-text" data-testid="imap-parse-result">
                      <p>{t('imap.parserTest.matchYes')}</p>
                      <p>{t('imap.parserTest.txId')}: {parseResult.extracted.txn_id ?? t('imap.parserTest.none')}</p>
                      <p>{t('imap.parserTest.amount')}: ${parseResult.extracted.amount ?? t('imap.parserTest.none')}</p>
                      {parseResult.would_link_to ? (
                        <p>{t('imap.parserTest.wouldLinkLabel')}: {parseResult.would_link_to}</p>
                      ) : (
                        <p className="text-pw-text-muted">{t('imap.parserTest.noMatchingRequest')}</p>
                      )}
                    </div>
                  ) : (
                    <p className="mt-2 text-pw-text-muted" data-testid="imap-parse-result">
                      {t('imap.parserTest.matchNo')}
                    </p>
                  )
                ) : (
                  <p className="mt-2 text-pw-text-muted">
                    {t('imap.parserTest.runHint')}
                  </p>
                )}
              </div>
            </div>
          </section>
        </>
      ) : null}

      {/* ---- Per-gateway regex matrix ---- */}
      {tab === 'matrix' ? (
        <section className="rounded-xl border border-pw-border bg-pw-bg p-6">
          <h2 className="text-lg font-semibold text-pw-text">{t('imap.matrix.heading')}</h2>
          <p className="mt-1 text-xs text-pw-text-muted">
            {t('imap.matrix.description')}
          </p>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-pw-border bg-pw-primary/5 text-left">
                  <th className="px-2 py-2 font-medium text-pw-text-muted">{t('imap.matrix.colGateway')}</th>
                  <th className="px-2 py-2 font-medium text-pw-text-muted">{t('imap.matrix.colSender')}</th>
                  <th className="px-2 py-2 font-medium text-pw-text-muted">{t('imap.matrix.colTxnId')}</th>
                  <th className="px-2 py-2 font-medium text-pw-text-muted">{t('imap.matrix.colAmount')}</th>
                </tr>
              </thead>
              <tbody>
                {config.gateways.map((row) => (
                  <tr key={row.gateway} className="border-b border-pw-border last:border-0">
                    <td className="px-2 py-2 align-top font-medium capitalize">{row.gateway}</td>
                    <td className="px-2 py-2">
                      <Input
                        value={row.sender_regex}
                        onChange={(e) =>
                          updateGatewayCell(row.gateway, 'sender_regex', e.target.value)
                        }
                        className="font-mono text-xs"
                        placeholder={t('imap.matrix.senderPlaceholder')}
                        data-testid={`regex-sender-${row.gateway}`}
                      />
                    </td>
                    <td className="px-2 py-2">
                      <Input
                        value={row.txn_id_regex}
                        onChange={(e) =>
                          updateGatewayCell(row.gateway, 'txn_id_regex', e.target.value)
                        }
                        className="font-mono text-xs"
                        placeholder={t('imap.matrix.txnIdPlaceholder')}
                      />
                    </td>
                    <td className="px-2 py-2">
                      <Input
                        value={row.amount_regex}
                        onChange={(e) =>
                          updateGatewayCell(row.gateway, 'amount_regex', e.target.value)
                        }
                        className="font-mono text-xs"
                        placeholder={t('imap.matrix.amountPlaceholder')}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {/* ---- Logs tab ---- */}
      {tab === 'logs' ? (
        <section className="rounded-xl border border-pw-border bg-pw-bg p-6">
          <h2 className="text-lg font-semibold text-pw-text">{t('imap.logs.heading')}</h2>
          {logsQuery.isLoading ? (
            <p className="mt-3 text-sm text-pw-text-muted">{t('imap.logs.loading')}</p>
          ) : logsQuery.isError ? (
            <p className="mt-3 text-sm text-red-600 dark:text-red-300">
              {logsQuery.error instanceof Error ? logsQuery.error.message : t('imap.logs.loadFailed')}
            </p>
          ) : !logsQuery.data || logsQuery.data.length === 0 ? (
            <p className="mt-3 text-sm text-pw-text-muted">{t('imap.logs.empty')}</p>
          ) : (
            <div className="overflow-x-auto">
            <table className="mt-4 w-full text-xs">
              <thead>
                <tr className="border-b border-pw-border text-left text-pw-text-muted">
                  <th className="py-2 font-medium">{t('imap.logs.colDate')}</th>
                  <th className="py-2 font-medium">{t('imap.logs.colSender')}</th>
                  <th className="py-2 font-medium">{t('imap.logs.colGateway')}</th>
                  <th className="py-2 font-medium">{t('imap.logs.colMatch')}</th>
                  <th className="py-2 font-medium">{t('imap.logs.colTxId')}</th>
                  <th className="py-2 font-medium">{t('imap.logs.colAmount')}</th>
                  <th className="py-2 font-medium">{t('imap.logs.colLinked')}</th>
                </tr>
              </thead>
              <tbody>
                {logsQuery.data.map((row, i) => (
                  <tr key={`${row.ts}-${i}`} className="border-b border-pw-border last:border-0">
                    <td className="py-2 text-pw-text-muted">
                      {new Date(row.ts).toLocaleString()}
                    </td>
                    <td className="py-2 font-mono">{row.sender}</td>
                    <td className="py-2">{row.gateway ?? '—'}</td>
                    <td className="py-2">
                      {row.matched ? (
                        <span className="rounded-full bg-emerald-100 dark:bg-emerald-950/40 px-2 py-0.5 text-emerald-700 dark:text-emerald-300">
                          {t('imap.logs.yes')}
                        </span>
                      ) : (
                        <span className="rounded-full bg-red-100 dark:bg-red-950/40 px-2 py-0.5 text-red-700 dark:text-red-300">{t('imap.logs.no')}</span>
                      )}
                    </td>
                    <td className="py-2 font-mono">{row.extracted.txn_id ?? '—'}</td>
                    <td className="py-2">{row.extracted.amount ?? '—'}</td>
                    <td className="py-2 font-mono text-pw-text-muted">
                      {row.linked_request_id ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </section>
      ) : null}

      {/* ---- Footer save (Connection + Matrix tabs) ---- */}
      {tab !== 'logs' ? (
        <div className="flex items-center justify-end gap-3">
          <Button
            variant="secondary"
            disabled={!dirty || saveMutation.isPending}
            onClick={() => {
              if (cfgQuery.data) setConfig(cfgQuery.data);
              setPassword('');
            }}
            data-testid="imap-discard"
          >
            {t('imap.discard')}
          </Button>
          <Button
            disabled={!dirty || saveMutation.isPending || !!sourceNamespaceError}
            onClick={() => saveMutation.mutate()}
            data-testid="imap-save"
          >
            {saveMutation.isPending ? t('imap.saving') : t('imap.save')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
